import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import {
  EvolviCoach,
  EvolviSeat,
  WithSubscriptionComponent,
  EnterpriseBasketService
} from '@sabstravtech/obtservices/angular';
import {
  faSeatAirline,
  faAngleLeft,
  faAngleRight,
  faArrowLeft,
  faArrowRight
} from '@fortawesome/pro-regular-svg-icons';

@Component({
  selector: 'app-evolvi-seatmap-coach',
  standalone: false,
  templateUrl: './evolvi-seatmap-coach.component.html',
  styleUrl: './evolvi-seatmap-coach.component.scss'
})
export class EvolviSeatmapCoachComponent extends WithSubscriptionComponent implements OnInit {
  faSeatAirline = faSeatAirline;
  faAngleLeft = faAngleLeft;
  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faAngleRight = faAngleRight;
  @Input() coachDetails: EvolviCoach;
  @Input() reservedSeats: any[] = [];
  @Input() key: any = null;
  @Input() defaultChosenSeat: any = null;
  @Input() maxSeats = 1;
  @Input() isLeftDirection = false;
  @Input() releventLeg;
  @Input() seatMapReversed = false;
  @Output() chosenSeat: EventEmitter<string[]> = new EventEmitter();
  chosenSeatIds = [];
  chosenSeatIdsObject = {};
  reservedSeatIds = {};
  imageLoadedCorrectly = false;
  loadingImage = true;
  seatMapUrl: string;
  outboundDirectionSeats = {};
  inboundDirectionSeats = {};

  constructor(private basketService: EnterpriseBasketService) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.determineSeatsDirection(this.coachDetails.seats);
    try {
      if (this.coachDetails.imageTemplate) {
        const escapedImageUrl = this.coachDetails.imageTemplate.replace(/[()]/g, (match) =>
          match === '(' ? '%28' : '%29'
        );
        const img = new Image();
        img.src = escapedImageUrl;
        await img.decode();
        this.imageLoadedCorrectly = true;
        this.resetSeats(); // ! - Sometimes we lose the chosen seat if the image is delayed in loading
        this.seatMapUrl = escapedImageUrl;
        this.loadingImage = false;
      } else {
        this.loadingImage = false;
      }
    } catch (error) {
      console.error(`Error decoding seatmap image: ${error}`);
      // ! - Cannot decode - probably due to a 404 error
      // Log the actual network response so we can diagnose *why* the decode failed,
      // instead of just seeing the browser's generic EncodingError.
      if (this.coachDetails.imageTemplate) {
        fetch(this.coachDetails.imageTemplate)
          .then((res) => {
            console.error(
              `Seatmap image fetch diagnostics - status: ${res.status}, ` +
                `content-type: ${res.headers.get('content-type')}, ` +
                `content-length: ${res.headers.get('content-length')}, ` +
                `type: ${res.type}, url: ${res.url}`
            );
          })
          .catch((fetchError) => {
            console.error(`Seatmap image fetch diagnostics - fetch itself failed: ${fetchError}`);
          });
      }
      this.loadingImage = false;
    }

    // console.log(`Relevent leg: ${JSON.stringify(this.releventLeg)}`);

    this.resetSeats();

    if (this.defaultChosenSeat?.length) {
      // console.log('default ', this.defaultChosenSeat);

      this.defaultChosenSeat.forEach((seat) => {
        const foundSeat = this.coachDetails.seats.find((innerSeat) => innerSeat.id === seat.seat);

        if (foundSeat && this.coachDetails.id === seat.coach) {
          this.chooseSeat(foundSeat);
        }
      });

      // this.chooseSeat(this.defaultChosenSeat.chosenSeat);
    }

    this.subscribe(this.basketService.chosenEvolviSeat, (seat) => {
      if (!seat) {
        this.resetSeats();
      }

      if (seat?.key === this.key) {
        // only for the same journey leg, otherwise ignore
        // console.log('New seat chosen: ', seat);
        if (seat?.coach?.id !== this.coachDetails?.id && seat.seat?.length) {
          // this.chosenSeatId = null;
          this.chosenSeatIds.shift();
          this.chosenSeat.emit(this.chosenSeatIds);
          this.setChosenObject();
        }
      }
    });

    this.coachDetails.seats.sort((a: EvolviSeat, b: EvolviSeat) => {
      // Ensure seats are rendering from left to right
      if (b.rowPosition > a.rowPosition) {
        return 1;
      } else if (a.rowPosition > b.rowPosition) {
        return -1;
      } else if (a.rowNumber > b.rowNumber) {
        return 1;
      } else if (b.rowNumber > a.rowNumber) {
        return -1;
      } else {
        return 0;
      }
    });
  }

  chooseSeat(seat: EvolviSeat): void {
    const curSeatsLength = Object.keys(this.chosenSeatIds).length;

    if (curSeatsLength === this.maxSeats && !this.chosenSeatIds.includes(seat.id)) {
      this.chosenSeatIds.shift();
    }

    if (this.chosenSeatIds.includes(seat.id)) {
      this.chosenSeatIds = this.chosenSeatIds.filter((id) => id !== seat.id);
      this.chosenSeat.emit(this.chosenSeatIds);
      this.basketService.chosenEvolviSeat.next({
        coach: this.coachDetails,
        seat: this.chosenSeatIds,
        key: this.key
      });
    } else {
      this.chosenSeatIds.push(seat.id);
      this.chosenSeat.emit(this.chosenSeatIds);
      this.basketService.chosenEvolviSeat.next({
        coach: this.coachDetails,
        seat: this.chosenSeatIds,
        key: this.key
      });
    }

    this.setChosenObject();
  }

  resetSeats() {
    console.log('+++ Reset Seats +++');

    this.chosenSeatIds = [];

    if (this.reservedSeats.length) {
      for (const seat of this.reservedSeats) {
        if (seat.coach === this.coachDetails.id && seat.legId === this.key) {
          // find the seat in the coachDetails as the objects are different
          const foundSeat = this.coachDetails.seats.find(
            (coachSeat) => coachSeat.id === seat.number
          );

          if (foundSeat) {
            this.reservedSeatIds[foundSeat.id] = true;
            if (!this.defaultChosenSeat) {
              this.chooseSeat(foundSeat);
            }
          }
        }
      }
    }
  }

  setChosenObject() {
    this.chosenSeatIdsObject = {};
    this.chosenSeatIds.forEach((id) => {
      this.chosenSeatIdsObject[id] = true;
    });
  }

  /**
   * @description - determine what direction the seats are pointing in
   * Email from Andy Edwards @ Evolvi - ODR = Outbound Direction, IDR = Inbound Direction
   * supposedly replace with Forwards or Backwards facing in Seat properties, but I can't see that
   * @param seats
   */
  determineSeatsDirection(seats: EvolviSeat[]): void {
    seats.forEach((seat) => {
      if (seat.propertyCodes.includes('ODR')) {
        this.outboundDirectionSeats[seat.id] = true;
      }

      if (seat.propertyCodes.includes('IDR')) {
        this.inboundDirectionSeats[seat.id] = true;
      }
    });
  }
}
